import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import Mail from '@wix/da-ds/pkg/Icons/16x16/Mail';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { pushAdoptsOrderOriginalDeviationModal } from '../../../../Modals/redux/actionCreators';
import { formatDollars } from '../../../../utils/formatting';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useEditDeviationAction } from '../../../../ActionBar/actionHooks/useEditDeviationAction';

import s from './OriginalInfo.scss';

interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const OriginalInfo: React.FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const custom_exclusives = useFlag('custom_exclusives');
  const dispatchPushAdoptsOrderOriginalDeviationModal = useAction(
    pushAdoptsOrderOriginalDeviationModal
  );
  const isOwner = useIsCurrentUserDeviationAuthor(deviation);
  const handleEditClick = useEditDeviationAction(deviation);

  const handleRequestClick = useCallback(() => {
    dispatchPushAdoptsOrderOriginalDeviationModal(deviation);
  }, [dispatchPushAdoptsOrderOriginalDeviationModal, deviation]);

  if (!custom_exclusives || !deviation.extended?.originals) {
    return null;
  }

  const { basePrice, estDeliveryDays, completedOrdersCount } =
    deviation.extended.originals;

  const formattedPrice = formatDollars(basePrice);
  const deliveryText = t('sidebar.originalInfo.delivery', {
    count: estDeliveryDays,
  });

  return (
    <div className={s['root']}>
      <div className={s['header']}>{t('sidebar.originalInfo.header')}</div>
      <div className={s['title']}>{deviation.title}</div>

      <div className={s['info-row']}>
        <div className={s['info-label']}>
          {t('sidebar.originalInfo.startingPrice')}
        </div>
        <div className={s['info-value']}>{formattedPrice}</div>
      </div>

      <div className={s['info-row']}>
        <div className={s['info-label']}>
          {t('sidebar.originalInfo.deliveryLabel')}
        </div>
        <div className={s['info-value']}>{deliveryText}</div>
      </div>

      {completedOrdersCount > 0 ? (
        <div className={s['info-row']}>
          <div className={s['info-label']}>
            {t('sidebar.originalInfo.completedOrders')}
          </div>
          <div className={s['info-value']}>
            {t('sidebar.originalInfo.orders', {
              count: completedOrdersCount,
            })}
          </div>
        </div>
      ) : null}

      {isOwner ? (
        <BrandedButton
          variant="brand-green"
          size="large"
          className={s['primary-button']}
          onClick={handleEditClick}
        >
          {t('common.edit')}
        </BrandedButton>
      ) : (
        <BrandedButton
          variant="originals"
          size="large"
          className={s['primary-button']}
          onClick={handleRequestClick}
        >
          {t('sidebar.originalInfo.requestNow')}
        </BrandedButton>
      )}

      {isOwner ? (
        <GhostButton
          variant="main"
          size="large"
          className={s['secondary-button']}
          rounded
        >
          {t('sidebar.originalInfo.manageOrders')}
        </GhostButton>
      ) : (
        <GhostButton
          variant="main"
          size="large"
          className={s['secondary-button']}
          icon={Mail}
          href={Url.userMessageLink(deviation.author?.username ?? '')}
          rounded
        >
          {t('sidebar.originalInfo.sendMessage')}
        </GhostButton>
      )}
    </div>
  );
};

OriginalInfo.displayName = 'OriginalInfo';

export default OriginalInfo;
